/**
 * One workflow, as a row of the `/workflows` table (DOP-478, restyled DOP-624):
 * icon, name with its kind and meta line, when it last ran, and how it runs
 * (Manual, Quarterly, On new files). Nothing else — what a workflow needs is
 * on the workflow itself.
 *
 * The row is one link, so the user aims at the workflow rather than at a word
 * inside it. The ⋯ menu (DOP-491) sits OUTSIDE that link: a button nested in
 * an anchor is not a thing a browser or a screen reader can make sense of.
 */

import {
	ClipboardList,
	FileSpreadsheet,
	FileText,
	LineChart,
	MoreHorizontal,
	Trash2,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { DashboardIndexRow, DashboardKind } from "../../context/DashboardsIndex";

const ICONS: Record<DashboardKind, React.ComponentType<{ className?: string }>> =
	{
		kpi: LineChart,
		monitoring: ClipboardList,
		loan_tape: FileSpreadsheet,
		deal_screening: FileText,
	};

export interface DashboardListRowProps {
	row: DashboardIndexRow;
	/**
	 * Ask to delete this workflow. The row raises the intent and nothing else —
	 * confirming and reporting belong to the list, which is the thing that has
	 * somewhere to put a dialog and an error, and which has to survive this row
	 * unmounting when the delete succeeds.
	 */
	onRequestDelete: (row: DashboardIndexRow) => void;
}

const DashboardListRow: React.FC<DashboardListRowProps> = ({
	row,
	onRequestDelete,
}) => {
	const Icon = ICONS[row.kind];
	const [menuOpen, setMenuOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	// Close on anything that means "I am done here" — a click elsewhere or
	// Escape. A menu that outlives the user's attention is a menu they will
	// click by accident.
	useEffect(() => {
		if (!menuOpen) return;
		const onPointerDown = (event: MouseEvent) => {
			if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") setMenuOpen(false);
		};
		document.addEventListener("mousedown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("mousedown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [menuOpen]);

	// Nothing has run yet: drawn quieter so the ones in use stand out.
	const quiet = row.lastRun === null;

	return (
		<li
			className="relative flex items-center gap-3 border-b border-[#f1f2f4] px-5 last:rounded-b-xl last:border-0 hover:bg-[#fafafa]"
			data-testid={`workflow-row-${row.key}`}
		>
			<Link
				to={row.href}
				className="flex min-w-0 flex-1 items-center gap-3.5 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782]"
			>
				<span
					className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
						quiet ? "bg-[#f2f2f2] text-[#9a9ea3]" : "bg-[#E6F3F3] text-[#004C4B]"
					}`}
				>
					<Icon className="h-4 w-4" aria-hidden />
				</span>
				<span className="min-w-0 flex-1">
					<span
						className={`block truncate text-[14px] font-semibold ${
							quiet ? "text-[#5B6472]" : "text-[#111111]"
						}`}
					>
						{row.name}
					</span>
					<span className="mt-0.5 block truncate text-xs text-[#6B7280]">
						{[row.typeLabel, row.meta].filter(Boolean).join(" · ")}
					</span>
				</span>
				<span className="hidden w-56 shrink-0 text-[13px] text-[#3f4247] md:block">
					{row.lastRun ?? <span className="text-[#9a9ea3]">Never run</span>}
				</span>
				<span className="w-32 shrink-0 text-[13px] text-[#3f4247]">{row.nextRun}</span>
			</Link>

			<div className="flex w-9 shrink-0 justify-end">
			{/* Quiet by design: the destructive action on a list of things people
			    open all day should not be as loud as opening one. */}
			<div className="relative" ref={menuRef}>
				<button
					type="button"
					onClick={() => setMenuOpen((open) => !open)}
					aria-haspopup="menu"
					aria-expanded={menuOpen}
					aria-label={`More options for ${row.name}`}
					className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-gray-100 hover:text-[#111111]"
				>
					<MoreHorizontal className="h-4 w-4" aria-hidden />
				</button>
				{menuOpen && (
					<div
						role="menu"
						aria-label={`Options for ${row.name}`}
						className="absolute right-0 z-10 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
					>
						<button
							type="button"
							role="menuitem"
							onClick={() => {
								setMenuOpen(false);
								onRequestDelete(row);
							}}
							className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
						>
							<Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
							Delete workflow
						</button>
					</div>
				)}
			</div>
			</div>
		</li>
	);
};

export default DashboardListRow;
